import React from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import { useSharingTools } from '../../../../hooks/useSharingTools';
import { type Platform, PLATFORM_CONFIG } from '../../../../shared/constants';
import { PlatformIcon } from '../../../common/PlatformIcon';
import { SharingToolsCardWrapperVariant1 } from '../../../SharingToolsCardWrapper';
import { ReadyToSharePostsCardLoading } from '../ReadyToSharePostsCardLoading';

/**
 * Displays a preview card for ready-to-share social media posts.
 * Shows a loading state when no post data is provided.
 */
export const ReadyToSharePostsCardPreviewVariant1: React.FC<Props> = ({ config }) => {
	const {
		platform = 'facebook',
		title = translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.ready_to_share.title'),
		description = translationKey(
			'corgi.petition.starter_dashboard_v2.sharing_tools.ready_to_share.description_variant1',
		),
	} = config ?? {};

	const { data, actions } = useSharingTools('petition_dashboard_grow_petition_v1', {
		imageVariantName: 'post_variant_a',
	});

	const { facebookPost, redditPost, instagramStory } = data.readyToSharePosts;

	// Get the preview image URL to display with the expected platform post variation, which is in shareVisual
	const previewImageUrl = data.shareVisual.image?.url || '';

	const postDataByPlatform: Record<Platform, typeof facebookPost> = {
		facebook: facebookPost,
		reddit: redditPost,
		instagram: instagramStory,
		twitter: undefined,
		whatsapp: undefined,
	};

	const post = postDataByPlatform[platform] || null;

	const { brandLabel } = PLATFORM_CONFIG[platform];

	return (
		<SharingToolsCardWrapperVariant1
			title={title}
			description={description}
			primaryActionOnClick={actions.navigateToReadyToSharePosts}
		>
			{post && previewImageUrl ? (
				<article
					data-platform={platform}
					data-testid={`ready-to-share-preview-${platform}`}
					className="relative h-[174px] w-[147px] flex-shrink-0"
					aria-label={`${brandLabel} ready-to-share post`}
				>
					<div className="relative h-full rounded-b-lg rounded-t-xl bg-[#403A49]">
						<div className="h-3 rounded-t-xl px-2.5 pt-1.5">
							<div className="flex gap-1.5">
								{/* Tab indicators */}
								<div className="h-px w-8 bg-white" />
								<div className="h-px w-8 bg-white opacity-50" />
								<div className="h-px w-8 bg-white opacity-50" />
							</div>
						</div>

						<div className="flex items-center gap-1 px-2 pb-1">
							<div className="h-3 w-3 rounded-full bg-[#766F81]" />
							<div className="h-1 w-14 rounded-full bg-[#766F81]" />
						</div>

						{/* Image area */}
						<img
							className="max-h-[146px] w-full rounded-b-lg object-cover shadow-sm"
							src={previewImageUrl}
							aria-label="Post preview image"
						/>
					</div>

					{/* Platform badge */}
					<PlatformIcon platform={platform} />
				</article>
			) : (
				<ReadyToSharePostsCardLoading platform={platform} />
			)}
		</SharingToolsCardWrapperVariant1>
	);
};

type Props = Readonly<{
	config?: {
		platform?: Platform;
		title?: TranslationKey;
		description?: TranslationKey;
	};
}>;
